import { useMutation, useQuery, useQueryClient, skipToken } from "@tanstack/vue-query";
import { fetchPartners, createPartner, updatePartner, PartnerQueryParams, Partner, fetchPartner, PartnerBody } from "@/data/actions-query";

import { partnersKeys } from "../queryHookKeys";
import { computed, Ref } from "vue";
import { ListView } from "@/types/api";

// Queries
export const useFetchPartner = (id: Ref<number | undefined>) => {
  const queryFn = computed(() => (id.value ? () => fetchPartner(id.value!) : skipToken));
  const queryKey = computed(() => partnersKeys.detail(id.value));
  return useQuery({
    enabled: !!id.value,
    queryKey,
    queryFn,
  });
};

export const useFetchPartners = (params: PartnerQueryParams) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: partnersKeys.list(),
    queryFn: async () => {
      const response = await fetchPartners(params);
      // Set detail pages as well
      response.items.forEach((partner) => {
        queryClient.setQueryData(partnersKeys.detail(partner.id), partner);
      });
      return response;
    },
  });
};

// Mutations
export const useCreatePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerBody) => createPartner(payload),
    onSuccess: (data: Partner) => {
      queryClient.setQueryData<ListView<Partner>>(partnersKeys.list(), (existingList) => {
        return {
          total: (existingList?.total ?? 0) + 1,
          items: [...(existingList?.items ?? []), data],
        };
      });
      queryClient.setQueryData(partnersKeys.detail(data.id), data);
    },
  });
};

export const useUpdatePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<PartnerBody> }) => updatePartner(id, payload),
    onSuccess: (data: Partner) => {
      queryClient.setQueryData<ListView<Partner>>(partnersKeys.list(), (existingList) => {
        if (!existingList) {
          // Something went wrong
          queryClient.invalidateQueries({ queryKey: partnersKeys.list() });
        } else {
          return {
            total: existingList.total,
            items: existingList.items.map((partner) => (partner.id === data.id ? data : partner)) ?? [data],
          };
        }
      });
      queryClient.setQueryData(partnersKeys.detail(data.id), data);
    },
  });
};
